import type {
  OneCConnectionCheck,
  OneCHookAgent,
  OneCHookAgentFailure,
  OneCProfile,
  OneCWebhookAttentionRetry,
  OneCWebhookDelivery,
  OneCWebhookSecret,
  OneCWebhookState,
} from '../model/types'

function fail(message: string): never {
  throw new Error(message)
}

function objectOf(raw: unknown, what: string): Record<string, unknown> {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) fail(`${what} — не объект`)
  return raw as Record<string, unknown>
}

function text(value: unknown, field: string): string {
  return typeof value === 'string' && value !== '' ? value : fail(`поле ${field} — не строка`)
}

/** `ResourceId` без конвертера сериализуется объектом `{ value }`. */
function idOf(value: unknown): string {
  if (typeof value === 'string') return text(value, 'resourceId')
  return text(objectOf(value, 'resourceId').value, 'resourceId.value')
}

export function decodeOneCProfile(raw: unknown): OneCProfile {
  const body = objectOf(raw, 'профиль 1С')
  if (typeof body.creationTemplateConfigured !== 'boolean') fail('поле creationTemplateConfigured — не логическое')
  if (!Array.isArray(body.capabilities)) fail('поле capabilities — не массив')
  return {
    resourceId: idOf(body.resourceId),
    displayName: text(body.displayName, 'displayName'),
    configurationVersion: text(body.configurationVersion, 'configurationVersion'),
    infobase: text(body.infobase, 'infobase'),
    configurationProfile: text(body.configurationProfile, 'configurationProfile'),
    creationTemplateConfigured: body.creationTemplateConfigured,
    capabilities: body.capabilities.map((item: unknown, index) => text(item, `capabilities[${index}]`)),
  }
}

export function decodeOneCConnectionCheck(raw: unknown): OneCConnectionCheck {
  const body = objectOf(raw, 'проверка связи 1С')
  if (typeof body.fromRememberedRefusal !== 'boolean') fail('поле fromRememberedRefusal — не логическое')
  const remoteStatus = body.remoteStatus
  if (remoteStatus !== undefined && remoteStatus !== null && typeof remoteStatus !== 'number')
    fail('поле remoteStatus — не число')
  const message = body.message
  if (message !== undefined && message !== null && typeof message !== 'string') fail('поле message — не строка')
  return {
    resourceId: idOf(body.resourceId),
    configurationVersion: text(body.configurationVersion, 'configurationVersion'),
    result: text(body.result, 'result'),
    fromRememberedRefusal: body.fromRememberedRefusal,
    remoteStatus: typeof remoteStatus === 'number' ? remoteStatus : null,
    message: typeof message === 'string' && message !== '' ? message : null,
  }
}

const WEBHOOK_STATES: readonly OneCWebhookState[] = ['no-secret', 'never-received', 'stale', 'receiving']

function count(value: unknown, field: string): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : fail(`поле ${field} — не целое неотрицательное число`)
}

function optionalText(value: unknown, field: string): string | null {
  if (value === undefined || value === null) return null
  return typeof value === 'string' ? value : fail(`поле ${field} — не строка`)
}

function flag(value: unknown, field: string): boolean {
  return typeof value === 'boolean' ? value : fail(`поле ${field} — не логическое`)
}

function optionalStatus(value: unknown, field: string): number | null {
  if (value === undefined || value === null) return null
  return count(value, field)
}

function decodeAgentFailure(raw: unknown, field: string): OneCHookAgentFailure | null {
  if (raw === undefined || raw === null) return null
  const body = objectOf(raw, field)
  return {
    since: text(body.since, `${field}.since`),
    status: optionalStatus(body.status, `${field}.status`),
    message: optionalText(body.message, `${field}.message`) ?? '',
  }
}

function decodeAgent(raw: unknown, index: number): OneCHookAgent {
  const field = `agents[${index}]`
  const body = objectOf(raw, field)
  return {
    sourceId: text(body.sourceId, `${field}.sourceId`),
    peer: text(body.peer, `${field}.peer`),
    channel: text(body.channel, `${field}.channel`),
    version: text(body.version, `${field}.version`),
    intervalSeconds: count(body.intervalSeconds, `${field}.intervalSeconds`),
    lastHeartbeatAt: text(body.lastHeartbeatAt, `${field}.lastHeartbeatAt`),
    silent: flag(body.silent, `${field}.silent`),
    queueBatches: count(body.queueBatches, `${field}.queueBatches`),
    queueBytes: count(body.queueBytes, `${field}.queueBytes`),
    oldestQueuedAt: optionalText(body.oldestQueuedAt, `${field}.oldestQueuedAt`),
    backlog: flag(body.backlog, `${field}.backlog`),
    lastDeliveredAt: optionalText(body.lastDeliveredAt, `${field}.lastDeliveredAt`),
    attentionBatches: count(body.attentionBatches, `${field}.attentionBatches`),
    needsAttention: flag(body.needsAttention, `${field}.needsAttention`),
    retrying: decodeAgentFailure(body.retrying, `${field}.retrying`),
    pause: decodeAgentFailure(body.pause, `${field}.pause`),
  }
}

/** `agents` нет — служба 1С старше 0.67.0 (сердцебиений агента не знает): агентов нет. Есть — строго массив по договору. */
function decodeAgents(raw: unknown): OneCHookAgent[] {
  if (raw === undefined) return []
  if (!Array.isArray(raw)) fail('поле agents — не массив')
  return raw.map((item: unknown, index) => decodeAgent(item, index))
}

export function decodeOneCWebhookDelivery(raw: unknown): OneCWebhookDelivery {
  const body = objectOf(raw, 'доставка вебхука 1С')
  const state = WEBHOOK_STATES.find((item) => item === body.state) ?? fail('поле state — неизвестное состояние доставки')
  const rejection = body.lastRejection === undefined || body.lastRejection === null ? null : objectOf(body.lastRejection, 'lastRejection')
  const failure = body.lastFailure === undefined || body.lastFailure === null ? null : objectOf(body.lastFailure, 'lastFailure')
  if (failure !== null && failure.state !== 'pending' && failure.state !== 'blocked') fail('поле lastFailure.state — не pending и не blocked')
  return {
    address: optionalText(body.address, 'address'),
    header: text(body.header, 'header'),
    activeSecrets: count(body.activeSecrets, 'activeSecrets'),
    state,
    staleAfterHours: count(body.staleAfterHours, 'staleAfterHours'),
    lastBatchAt: optionalText(body.lastBatchAt, 'lastBatchAt'),
    batches24h: count(body.batches24h, 'batches24h'),
    products24h: count(body.products24h, 'products24h'),
    pendingBatches: count(body.pendingBatches, 'pendingBatches'),
    lastRejection:
      rejection === null
        ? null
        : { at: text(rejection.at, 'lastRejection.at'), status: count(rejection.status, 'lastRejection.status'), code: optionalText(rejection.code, 'lastRejection.code') ?? '' },
    lastFailure:
      failure === null
        ? null
        : {
            receivedAt: text(failure.receivedAt, 'lastFailure.receivedAt'),
            state: failure.state as 'pending' | 'blocked',
            code: text(failure.code, 'lastFailure.code'),
            message: optionalText(failure.message, 'lastFailure.message'),
          },
    attentionCount: body.attentionCount === undefined || body.attentionCount === null ? null : count(body.attentionCount, 'attentionCount'),
    agents: decodeAgents(body.agents),
    at: text(body.at, 'at'),
  }
}

export function decodeOneCWebhookAttentionRetry(raw: unknown): OneCWebhookAttentionRetry {
  const body = objectOf(raw, 'возврат пакетов вебхука 1С')
  return { requeued: count(body.requeued, 'requeued') }
}

export function decodeOneCWebhookSecret(raw: unknown): OneCWebhookSecret {
  const body = objectOf(raw, 'секрет вебхука 1С')
  return {
    secret: text(body.secret, 'secret'),
    header: text(body.header, 'header'),
    createdAt: text(body.createdAt, 'createdAt'),
    revoked: body.revoked === undefined ? 0 : count(body.revoked, 'revoked'),
  }
}
